<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import { useAlert, useTrack } from 'dashboard/composables';
import ReportsAPI from 'dashboard/api/reports';
import { generateStatisticsFileName } from 'dashboard/helper/downloadHelper';
import { REPORTS_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import ReportHeader from './components/ReportHeader.vue';
import ReportFiltersSection from './components/ReportFiltersSection.vue';
import AgentActivityMetricCards from './components/AgentActivityMetricCards.vue';
import AgentActivityCharts from './components/AgentActivityCharts.vue';
import AgentActivitySummaryTable from './components/AgentActivitySummaryTable.vue';
import V4Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { parseSummaryEntityFilterParams } from './helpers/reportFilterHelper';

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const route = useRoute();

const loading = ref(false);
const from = ref(0);
const to = ref(0);
const comparisonPeriods = ref([]);
const overview = ref({});
const agents = ref([]);
const onlineByDay = ref([]);
const statusComposition = ref({});
const selectedDay = ref(null);
const onlyWithStatusChanges = ref(false);
const selectedAgentIds = ref([]);
const selectedTeamIds = ref([]);

const parsed = parseSummaryEntityFilterParams(route.query);
selectedAgentIds.value = parsed.agentIds || [];
selectedTeamIds.value = parsed.teamIds || [];

const filteredAgents = computed(() => {
  let rows = agents.value || [];
  if (onlyWithStatusChanges.value) {
    rows = rows.filter(row => Number(row.status_change_count || 0) > 0);
  }
  if (selectedDay.value) {
    rows = rows.map(row => {
      const day = (row.daily || []).find(d => d.date === selectedDay.value);
      if (!day) {
        return {
          ...row,
          accepting_seconds: 0,
          not_accepting_seconds: 0,
          online_seconds: 0,
          offline_seconds: 0,
          status_change_count: 0,
          online_share: 0,
        };
      }
      const periodSeconds = Math.max(1, Number(to.value) - Number(from.value));
      const daySeconds =
        day.accepting + day.not_accepting + day.offline || periodSeconds;
      return {
        ...row,
        accepting_seconds: day.accepting,
        not_accepting_seconds: day.not_accepting,
        online_seconds: day.online,
        offline_seconds: day.offline,
        status_change_count: day.status_change_count,
        online_share: daySeconds
          ? Number(((day.online / daySeconds) * 100).toFixed(1))
          : 0,
      };
    });
  }
  return rows;
});

const fetchReports = async () => {
  if (!from.value || !to.value) return;
  loading.value = true;
  try {
    const { data } = await ReportsAPI.getAgentActivity({
      from: from.value,
      to: to.value,
      agentIds: selectedAgentIds.value,
      teamIds: selectedTeamIds.value,
    });
    overview.value = data.overview || {};
    agents.value = data.agents || [];
    onlineByDay.value = data.online_by_day || [];
    statusComposition.value = data.status_composition || {};
  } catch {
    useAlert(t('REPORT.DATA_FETCHING_FAILED'));
    overview.value = {};
    agents.value = [];
    onlineByDay.value = [];
    statusComposition.value = {};
  } finally {
    loading.value = false;
  }
};

const onFiltersApply = ({ date, entity }) => {
  from.value = date.from;
  to.value = date.to;
  comparisonPeriods.value = date.comparisonPeriods || [];
  selectedAgentIds.value = entity.agentIds || [];
  selectedTeamIds.value = entity.teamIds || [];
  selectedDay.value = null;
  fetchReports();
  useTrack(REPORTS_EVENTS.FILTER_REPORT, {
    filterType: 'date',
    reportType: 'agent_activity',
  });
};

const onStatusChangesClick = () => {
  onlyWithStatusChanges.value = !onlyWithStatusChanges.value;
};

const onOnlineDayClick = date => {
  selectedDay.value = selectedDay.value === date ? null : date;
};

const openDetail = userId => {
  router.push({
    name: 'agent_activity_detail',
    params: { accountId: route.params.accountId, id: userId },
    query: { from: from.value, to: to.value },
  });
};

const downloadReports = () => {
  const fileName = generateStatisticsFileName({
    section: 'activity',
    from: from.value,
    to: to.value,
  });
  store.dispatch('downloadAgentActivityReports', {
    from: from.value,
    to: to.value,
    fileName,
    agentIds: selectedAgentIds.value,
    teamIds: selectedTeamIds.value,
    comparisonPeriods: comparisonPeriods.value,
  });
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <ReportHeader :header-title="$t('AGENT_ACTIVITY_REPORT.HEADER')">
      <V4Button
        :label="$t('AGENT_ACTIVITY_REPORT.DOWNLOAD')"
        icon="i-ph-file-arrow-down"
        size="sm"
        @click="downloadReports"
      />
    </ReportHeader>
    <p class="text-sm text-n-slate-11">
      {{ $t('AGENT_ACTIVITY_REPORT.DESCRIPTION') }}
    </p>

    <ReportFiltersSection
      type="agent_activity"
      :disabled="loading"
      hide-resolution-method
      @filters-apply="onFiltersApply"
    />

    <AgentActivityMetricCards
      :overview="overview"
      :status-changes-active="onlyWithStatusChanges"
      @status-changes-click="onStatusChangesClick"
    />

    <AgentActivityCharts
      :agents="filteredAgents"
      :online-by-day="onlineByDay"
      :status-composition="statusComposition"
      :from="from"
      :to="to"
      :selected-day="selectedDay"
      @day-click="onOnlineDayClick"
      @open-detail="openDetail"
    />

    <AgentActivitySummaryTable
      :rows="filteredAgents"
      :selected-day="selectedDay"
      @open-detail="openDetail"
    />

    <div
      v-if="loading"
      class="fixed inset-0 z-40 flex items-start justify-center pt-[12.5rem] bg-n-solid-1/40 pointer-events-none"
    >
      <Spinner :size="32" class="text-n-brand" />
    </div>
  </div>
</template>
